import {type MouseEvent, useEffect, useState} from 'react';
import {Content} from '~/GalleryPB/components/Template/components/QuickView/components/Content/Content';
import {useTemplateHelpers} from '~/GalleryPB/hooks/useTemplateHelpers';
import {type Template, VariantType} from '~/GalleryPB/types/template';
import {Button} from '~library/atoms/Button';
import {useIsLargeAndUp, useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {BottomSheet} from '~library/molecules/BottomSheet';
import {ResponsiveModal} from '~library/organisms/ResponsiveModal';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {Footer} from './components/Footer/Footer';
import {FreePremiumToggle} from './components/FreePremiumToggle/FreePremiumToggle';
import {Header} from './components/Header/Header';
import {QuickViewAnimation} from './components/QuickViewAnimation/QuickViewAnimation';

import './QuickView.sass';

type QuickViewProps = {
  open: boolean;
  hideCtas: boolean;
  onClose: () => void;
  template: Template;
  onStartCustomizingClick: (e: MouseEvent<HTMLButtonElement>, variant: VariantType) => void;
  openQuickView: (templateId: string) => void;
};

export const QuickView = ({
  open,
  template,
  onClose,
  onStartCustomizingClick,
  openQuickView,
  hideCtas,
}: QuickViewProps) => {
  const isMediumAndUp = useIsMediumAndUp();
  const isLargeAndUp = useIsLargeAndUp();
  const [variant, setVariant] = useState<VariantType>(VariantType.PREMIUM);
  const [highlightFree, setHighlightFree] = useState(false);
  const isPremiumTemplate = template.premium;
  const {checkIfQuickViewEnabled} = useTemplateHelpers();
  const isQuickViewEnabled = checkIfQuickViewEnabled(template);

  useEffect(() => {
    document.body.classList.toggle('no-scroll', open);

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [open]);

  // set event to free for specific event types
  useEffect(() => {
    if (isQuickViewEnabled) {
      if (!isPremiumTemplate) {
        setVariant(VariantType.FREE);
        setHighlightFree(true);
      }
    }
  }, [isQuickViewEnabled, isPremiumTemplate]);

  const renderModalContent = () => (
    <>
      <div className="quick-view__close-button-wrapper">
        <Button
          variant="unstyled-button"
          onClick={onClose}
          data-qa-id="pdp-go-premium"
          data-impression-tracking="true"
        >
          <CloseLineIcon ratio={0.75} />
        </Button>
      </div>
      <div className="quick-view__wrapper">
        {isLargeAndUp && <QuickViewAnimation template={template} variant={variant} key={variant} />}

        <div className="quick-view">
          {!isPremiumTemplate && <FreePremiumToggle setVariant={setVariant} variant={variant} />}

          {!isLargeAndUp && (
            <QuickViewAnimation template={template} variant={variant} key={variant} />
          )}

          <Header
            title={template.displayName}
            isPremiumTemplate={isPremiumTemplate}
            templateId={template.templateId}
            onClose={onClose}
            openQuickView={openQuickView}
          />

          <Content variant={variant} />

          {!hideCtas && (
            <Footer
              variant={variant}
              onStartCustomizingClick={onStartCustomizingClick}
              isPremiumTemplate={isPremiumTemplate}
              highlightFree={highlightFree}
            />
          )}
        </div>
      </div>
    </>
  );

  if (!open) return null;

  if (isMediumAndUp) {
    return (
      <ResponsiveModal
        isModal
        isOpen={open}
        onClose={onClose}
        overlayClassName="quick-view__responsive-modal"
        showHeader={false}
        closeOnClickOutside
      >
        {renderModalContent()}
      </ResponsiveModal>
    );
  }

  return (
    <BottomSheet in={open} className="quick-view__bottom-sheet" content={renderModalContent()} />
  );
};
